import {
  CheckIcon,
  Combobox,
  Group,
  Space,
  Text,
  useCombobox,
} from '@mantine/core'
import type {
  CarePlan,
  MedicationRequest,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import React, { type JSX, useState } from 'react'
import { statusToColor } from '#src/components/display/constants.ts'
import { StatusBadge } from '#src/components/display/StatusBadge.tsx'
import { useFhirPatchMutation } from '#src/hooks/useFhirPatchMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

type StatusType =
  | CarePlan['status']
  | Task['status']
  | MedicationRequest['status']

const ResourceStatuses = Object.keys(statusToColor) as StatusType[]

export interface ResourceStatusProps {
  readonly resource: ServiceRequest | CarePlan | Task | MedicationRequest
  readonly statuses?: StatusType[]
}

export function ResourceStatus(props: ResourceStatusProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const resource = props.resource
  const patch = useFhirPatchMutation(resource.resourceType)
  const [selectedItem, setSelectedItem] = useState<StatusType>(
    resource?.status ?? 'unknown'
  )
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const statuses = props.statuses ?? ResourceStatuses

  const options = statuses.map((item) => (
    <Combobox.Option
      value={item}
      key={item}
      onClick={(event: React.MouseEvent) => {
        // Prevent the event from bubbling up to the parent and triggering navigation
        event.stopPropagation()
      }}
    >
      <Group>
        {item === selectedItem ? (
          <CheckIcon size={12} />
        ) : (
          <Space w={12} h={12} />
        )}
        <Text c={statusToColor[item]} tt="uppercase" size="xs">
          {item}
        </Text>
      </Group>
    </Combobox.Option>
  ))

  return (
    <Combobox
      store={combobox}
      width={250}
      position="bottom-start"
      withArrow
      onOptionSubmit={(value) => {
        const status = value as CarePlan['status']
        setSelectedItem(status)
        void updateStatus(status)
        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        <StatusBadge
          status={selectedItem}
          onClick={
            isReadOnly
              ? undefined
              : (event: React.MouseEvent): void => {
                  combobox.toggleDropdown()
                  event.preventDefault()
                  event.stopPropagation()
                }
          }
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )

  async function updateStatus(status: StatusType): Promise<void> {
    invariant(resource?.id, 'Resource is required')
    patch.mutate({
      resource,
      mutateFn(resource) {
        resource.status = status
      },
    })
  }
}
